'use client';

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { Download, ThumbsUp, ThumbsDown, Activity, Briefcase, Clock, FileText, Gauge, Landmark, MessageCircle, MessageSquare, Send, Sparkles, Store } from '@/components/ui/lucide-memo';
import { EntityBulkAction, EntityRowAction, PortfolioFitDTO, PortfolioFitFilters } from '#types/types';
import { toast } from 'sonner';
import { Table, TableColumnDef, type TableContextMenuAction, type TableFilterOption, type TableRowSelectionContext } from '../../../portal/Table';
import { buildCsvContent, downloadCsv } from '@/lib/portal/csvExport';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { supportsInventoryViewModeToggle } from '@/components/portal/inventoryViewModeCapabilities';
import { prospectionColumnHeaders } from '@/components/entities/prospectionCells';

const translations = {
  en: {
    sortScore: 'Score',
    sortUpdatedAt: 'Last updated',
    sortCreatedAt: 'Created date',
    portfolio: 'Portfolio',
    brand: 'Brand',
    signal: 'Signal',
    fit: 'Fit',
    status: 'Status',
    assets: 'In assets',
    evaluated: 'Evaluated',
    campaign: 'Campaign',
    markInteresting: 'Mark interesting',
    markDismissed: 'Mark dismissed',
    exportCsv: 'Export CSV',
    exportCsvSuccess: (count: number) => `${count} portfolio fit(s) exported.`,
    exportCsvFailed: 'Failed to export selected portfolio fits.',
  },
  fr: {
    sortScore: 'Score',
    sortUpdatedAt: 'Derniere maj',
    sortCreatedAt: 'Date creation',
    portfolio: 'Portfolio',
    brand: 'Marque',
    signal: 'Signal',
    fit: 'Fit',
    status: 'Statut',
    assets: 'Dans les actifs',
    evaluated: 'Évalué',
    campaign: 'Campagne',
    markInteresting: 'Marquer interessant',
    markDismissed: 'Marquer ecarte',
    exportCsv: 'Exporter CSV',
    exportCsvSuccess: (count: number) => `${count} portfolio fit(s) exporte(s).`,
    exportCsvFailed: "Echec de l'export des portfolio fits selectionnes.",
  },
} as const;

const EXPANDED_ROW_STYLE: React.CSSProperties = { width: 'var(--table-viewport-width, 100%)' };

interface PortfolioFitsTableProps {
  initialFilters?: Partial<PortfolioFitFilters>;
  lockedFilters?: Partial<PortfolioFitFilters>;
  scopedIds?: string[] | null;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  renderCustomToolbarHeader?: () => React.ReactNode;
  toolbarContextMenuActions?: TableContextMenuAction[];
  disableManualRefresh?: boolean;
  onManualRefresh?: () => void | Promise<void>;
  bulkActions?: EntityBulkAction<PortfolioFitDTO>[];
  viewModeVisual?: InventoryVisualMode;
  onViewModeVisualChange?: (mode: InventoryVisualMode) => void;
  inlineExpand?: boolean;
  onExpandedIdChange?: (id: string | null) => void;
  /** Controls the expanded row; forwarded to Table so the store can own it. */
  expandedId?: string | null;
  /** When true, the table re-queries on an interval so a running campaign keeps filling in. */
  liveRefresh?: boolean;
  /** Lift checkbox selection to a parent (e.g. the kernel store) so the right bar can bulk-act on it. */
  controlledSelectionIds?: string[];
  onControlledSelectionChange?: (ids: string[]) => void;
}

export const PortfolioFitsTable = React.memo(({
  initialFilters,
  lockedFilters,
  scopedIds,
  assetLedgerTitle = 'Portfolio fits',
  assetLedgerBreadcrumb = 'Portfolio / Fits',
  renderCustomToolbarHeader,
  toolbarContextMenuActions,
  disableManualRefresh,
  onManualRefresh,
  bulkActions,
  viewModeVisual,
  onViewModeVisualChange,
  inlineExpand,
  onExpandedIdChange,
  expandedId,
  liveRefresh,
  controlledSelectionIds,
  onControlledSelectionChange,
}: PortfolioFitsTableProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const userContext = useKernelStore((state) => state.userContext);
  const t = translations[lang];
  const [localViewModeVisual, setLocalViewModeVisual] = useState<InventoryVisualMode>('grid');
  const resolvedViewModeVisual = viewModeVisual ?? localViewModeVisual;
  const handleViewModeVisualChange = useCallback((mode: InventoryVisualMode) => {
    onViewModeVisualChange?.(mode);
    if (!onViewModeVisualChange) {
      setLocalViewModeVisual(mode);
    }
  }, [onViewModeVisualChange]);

  const [visiblePortfolioFitIds, setVisiblePortfolioFitIds] = useState<string[]>([]);
  const handleVisibleIdsChange = useCallback((ids: string[]) => setVisiblePortfolioFitIds(ids), []);

  const portfolioFitLastActivity = useKernelStore((state) => state.inventory.PortfolioFit.lastActivity);

  useEffect(() => {
    const k = kernel.current;
    if (!k || visiblePortfolioFitIds.length === 0) return;

    const inv = k.getInventory();
    const fitReg = inv.PortfolioFit.registry;
    const brandReg = inv.Brand.registry;
    const portfolioReg = inv.Portfolio.registry;
    const campaignReg = inv.PortfolioFitCampaign.registry;

    const brandIdsToFetch = new Set<string>();
    const portfolioIdsToFetch = new Set<string>();
    const campaignIdsToFetch = new Set<string>();

    visiblePortfolioFitIds.forEach((id) => {
      const fit = fitReg[id];
      if (!fit) return;
      if (fit.brandId && !brandReg[fit.brandId]) brandIdsToFetch.add(fit.brandId);
      if (fit.portfolioId && !portfolioReg[fit.portfolioId]) portfolioIdsToFetch.add(fit.portfolioId);
      if (fit.campaignId && !campaignReg[fit.campaignId]) campaignIdsToFetch.add(fit.campaignId);
    });

    if (brandIdsToFetch.size > 0) k.ensureInventoryIds('Brand', Array.from(brandIdsToFetch));
    if (portfolioIdsToFetch.size > 0) k.ensureInventoryIds('Portfolio', Array.from(portfolioIdsToFetch));
    if (campaignIdsToFetch.size > 0) k.ensureInventoryIds('PortfolioFitCampaign', Array.from(campaignIdsToFetch));
  }, [visiblePortfolioFitIds, portfolioFitLastActivity, kernel]);

  const sortOptions = useMemo(
    () => [
      { label: t.sortScore, field: 'score' },
      { label: t.sortUpdatedAt, field: 'updatedAt' },
      { label: t.sortCreatedAt, field: 'createdAt' },
    ],
    [t.sortCreatedAt, t.sortScore, t.sortUpdatedAt]
  );

  const filterOptions = useMemo<TableFilterOption<'PortfolioFit'>[]>(
    () => [
      { key: 'brandName', label: t.brand },
      { key: 'status', label: t.status },
      { key: 'score', label: t.fit },
      { key: 'campaignId', label: t.campaign },
    ],
    [t.brand, t.campaign, t.fit, t.status]
  );

  const hidePortfolioColumn = Boolean(lockedFilters?.portfolioId || initialFilters?.portfolioId);

  const columns = useMemo<TableColumnDef[]>(() => {
    const ph = prospectionColumnHeaders(lang);
    const allColumns: (TableColumnDef & { isHidden?: boolean })[] = [
      { id: 'portfolio', icon: Briefcase, minWidth: 180, header: t.portfolio, isHidden: hidePortfolioColumn },
      { id: 'brand', icon: Store, minWidth: 200, header: t.brand },
      { id: 'signal', icon: Sparkles, minWidth: 300, header: t.signal },
      { id: 'fit', icon: Gauge, minWidth: 80, header: t.fit },
      { id: 'status', icon: Activity, minWidth: 90, header: t.status },
      { id: 'assets', icon: Landmark, minWidth: 160, header: t.assets },
      { id: 'evaluated', icon: Clock, minWidth: 120, header: lang === 'fr' ? 'Temps' : 'Time' },
      { id: 'prospectionContacted', icon: Send, minWidth: 90, header: ph.contacted },
      { id: 'prospectionChannel', icon: MessageSquare, minWidth: 110, header: ph.channel },
      { id: 'prospectionResponse', icon: MessageCircle, minWidth: 110, header: ph.response },
      { id: 'prospectionComment', icon: FileText, minWidth: 220, header: ph.comment },
    ];
    return allColumns.filter((column) => !column.isHidden);
  }, [hidePortfolioColumn, t, lang]);

  const effectiveInitialFilters = useMemo<Partial<PortfolioFitFilters>>(
    () =>
      initialFilters === undefined
        ? {
            sortBy: 'score',
            sortDir: 'desc',
          }
        : {
            ...initialFilters,
          },
    [initialFilters]
  );

  const canModeratePortfolioFit = useCallback((portfolioFit: PortfolioFitDTO) => {
    if (userContext.isFaterAdmin) return true;
    if (userContext.isOrganisationAdmin) return true;
    return Boolean(portfolioFit.portfolioId);
  }, [userContext.isFaterAdmin, userContext.isOrganisationAdmin]);

  const rowActions = useMemo<EntityRowAction<PortfolioFitDTO>[]>(() => [
    {
      id: 'flag-portfolio-fit-interesting',
      label: t.markInteresting,
      icon: <ThumbsUp size={13} strokeWidth={2} />,
      visible: canModeratePortfolioFit,
      onClick: async (portfolioFit) => {
        if (!kernel.current) return;
        await kernel.current.flagPortfolioFitInteresting(portfolioFit.id);
      },
    },
    {
      id: 'flag-portfolio-fit-dismissed',
      label: t.markDismissed,
      icon: <ThumbsDown size={13} strokeWidth={2} />,
      visible: canModeratePortfolioFit,
      onClick: async (portfolioFit) => {
        if (!kernel.current) return;
        await kernel.current.flagPortfolioFitDismissed(portfolioFit.id);
      },
    },
  ], [
    canModeratePortfolioFit,
    kernel,
    t.markInteresting,
    t.markDismissed,
  ]);

  const buildPortfolioFitCsvContent = useCallback((
    portfolioFits: PortfolioFitDTO[],
    lookup: {
      brandRegistry: Record<string, { name?: string | null; category?: string | null; websiteUrl?: string | null } | undefined>;
      portfolioRegistry: Record<string, { name?: string | null } | undefined>;
      campaignRegistry: Record<string, { id?: string } | undefined>;
    }
  ): string => {
    const headers = [
      'portfolioName',
      'brandName',
      'brandCategory',
      'brandWebsiteUrl',
      'fitScore',
      'fitStatus',
      'campaignId',
      'createdAt',
      'updatedAt',
      'rationale',
    ];

    const rows = portfolioFits.map((fit) => {
      const brand = lookup.brandRegistry[fit.brandId];
      const portfolio = lookup.portfolioRegistry[fit.portfolioId];
      const campaignId =
        typeof fit.campaignId === 'string' && fit.campaignId.length > 0
          ? fit.campaignId
          : (lookup.campaignRegistry[fit.campaignId || '']?.id ?? '');

      return [
        fit.portfolioName || portfolio?.name || '',
        fit.brandName || brand?.name || '',
        brand?.category ?? '',
        brand?.websiteUrl ?? '',
        fit.score,
        fit.status,
        campaignId,
        fit.createdAt,
        fit.updatedAt,
        fit.rationale ?? '',
      ];
    });

    return buildCsvContent(headers, rows);
  }, []);

  const downloadPortfolioFitCsv = useCallback((portfolioFits: PortfolioFitDTO[]) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel unavailable');
    if (!portfolioFits.length) return;
    const complete = k.beginSourceAction?.({ action: 'export', origin: 'human', data: { entityType: 'PortfolioFit', count: portfolioFits.length, format: 'csv' } });
    try {
    const inventory = k.getInventory();
    const csv = buildPortfolioFitCsvContent(portfolioFits, {
      brandRegistry: inventory.Brand.registry as Record<string, { name?: string | null; category?: string | null; websiteUrl?: string | null } | undefined>,
      portfolioRegistry: inventory.Portfolio.registry as Record<string, { name?: string | null } | undefined>,
      // The unified table: the family registry this used to read has held no rows since the
      // campaign tables were merged, so every campaign column in the export came out empty.
      campaignRegistry: inventory.Campaign.registry as Record<string, { id?: string } | undefined>,
    });
    downloadCsv('fater_portfolio_fits_export', csv);
    complete?.('download_started');
    } catch (error) {
      complete?.('client_failed');
      throw error;
    }
  }, [buildPortfolioFitCsvContent, kernel]);

  const exportBulkAction = useMemo<EntityBulkAction<PortfolioFitDTO>>(() => ({
    id: 'export-selected-portfolio-fits-csv',
    label: t.exportCsv,
    icon: <Download size={13} />,
    disabled: (selectedPortfolioFits) => selectedPortfolioFits.length === 0,
    onClick: async (selectedPortfolioFits) => {
      try {
        if (!selectedPortfolioFits.length) return;
        downloadPortfolioFitCsv(selectedPortfolioFits);
        toast.success(t.exportCsvSuccess(selectedPortfolioFits.length));
      } catch (error) {
        console.error('[PortfolioFitsTable] CSV export failed', error);
        toast.error(t.exportCsvFailed);
      }
    },
  }), [downloadPortfolioFitCsv, t.exportCsv, t.exportCsvFailed, t.exportCsvSuccess]);

  const effectiveBulkActions = useMemo<EntityBulkAction<PortfolioFitDTO>[]>(() => {
    const provided = Array.isArray(bulkActions) ? bulkActions : [];
    const defaults = [exportBulkAction];
    return [...defaults.filter((action) => !provided.some((p) => p.id === action.id)), ...provided];
  }, [bulkActions, exportBulkAction]);

  const canShowViewModeToggle = supportsInventoryViewModeToggle('PortfolioFit');
  const composedToolbarHeader = useCallback(() => {
    if (!canShowViewModeToggle && !renderCustomToolbarHeader) return null;
    return (
      <>
        {canShowViewModeToggle && (
          <ViewModeGraphicToggle
            mode={resolvedViewModeVisual}
            onModeChange={handleViewModeVisualChange}
            className={renderCustomToolbarHeader ? 'mr-1' : undefined}
          />
        )}
        {renderCustomToolbarHeader?.()}
      </>
    );
  }, [
    canShowViewModeToggle,
    handleViewModeVisualChange,
    renderCustomToolbarHeader,
    resolvedViewModeVisual,
  ]);
  const effectiveToolbarHeader =
    canShowViewModeToggle || renderCustomToolbarHeader ? composedToolbarHeader : undefined;

  const renderPortfolioFitRow = useCallback((
    fit: PortfolioFitDTO,
    isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
    selectionContext?: TableRowSelectionContext,
  ) => (
    <div className="flex w-full min-w-[var(--table-min-width)] flex-none flex-col">
      <EntityView
        key={fit.id}
        type="PortfolioFit"
        entityId={fit.id}
        variant="row"
        onClick={toggle}
        columns={currentColumns}
        rowActions={rowActions}
        isSelectable={selectionContext?.isSelectable}
        isSelected={selectionContext?.isSelected}
        onToggleSelect={selectionContext?.onToggleSelect}
        onSelectionZoneMouseDown={selectionContext?.onSelectionZoneMouseDown}
        className={isExpanded ? 'bg-[#222222] rounded-t-[10px]' : ''}
      />
      {isExpanded && (
        <div className="sticky left-0 z-[1]" style={EXPANDED_ROW_STYLE}>
          <EntityView
            type="PortfolioFit"
            entityId={fit.id}
            variant="page"
            pageHasEntityRowAbove
            className="h-auto overflow-hidden rounded-b-[10px] border-b border-[#2A2A2A] !ml-0"
          />
        </div>
      )}
    </div>
  ), [rowActions]);

  return (
    <Table
      entityType="PortfolioFit"
      initialFilters={effectiveInitialFilters}
      lockedFilters={lockedFilters}
      scopedIds={scopedIds}
      disableManualRefresh={disableManualRefresh}
      onManualRefresh={onManualRefresh}
      liveRefresh={liveRefresh}
      filterOptions={filterOptions}
      columns={columns}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      sortOptions={sortOptions}
      renderToolbarExtras={effectiveToolbarHeader}
      toolbarContextMenuActions={toolbarContextMenuActions}
      inlineExpand={inlineExpand}
      onExpandedIdChange={onExpandedIdChange}
      expandedId={expandedId}
      controlledSelectionIds={controlledSelectionIds}
      onControlledSelectionChange={onControlledSelectionChange}
      onVisibleItemIdsChange={handleVisibleIdsChange}
      bulkActions={effectiveBulkActions}
      rowActions={rowActions}
      renderRow={renderPortfolioFitRow}
    />
  );
});

PortfolioFitsTable.displayName = 'PortfolioFitsTable';
